import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArchiveRestore, Download, Plus, Search, Trash2 } from "lucide-react";
import api, { fmtDateTime } from "@/api";
import { useAuth } from "@/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const MODULES = {
  customers: { title: "Customers", fields: [["name", "Customer name", "text", true], ["phone", "Phone", "tel"], ["email", "Email", "email"], ["company", "Company", "text"], ["address", "Address", "text"], ["tags", "Tags", "text"], ["notes", "Notes", "text"]] },
  vendors: { title: "Vendors / Suppliers", fields: [["name", "Vendor name", "text", true], ["phone", "Phone", "tel"], ["email", "Email", "email"], ["company", "Company", "text"], ["tax_id", "Tax ID", "text"], ["payment_terms", "Payment terms", "text"], ["address", "Address", "text"], ["notes", "Notes", "text"]] },
  amc_warranty: { title: "AMC & Warranty", fields: [["customer_name", "Customer name", "text", true], ["phone", "Phone", "tel"], ["contract_no", "Contract number", "text"], ["service", "Service / Product", "text"], ["start_date", "Start date", "date"], ["end_date", "End date", "date"], ["amount", "Amount", "number"], ["status", "Status", "text"], ["notes", "Notes", "text"]] },
  tasks_reminders: { title: "Tasks & Reminders", fields: [["title", "Task title", "text", true], ["description", "Details", "text"], ["due_date", "Due date", "date"], ["due_time", "Due time", "time"], ["assigned_to", "Assigned to", "text"], ["status", "Status", "text"], ["priority", "Priority", "text"], ["related_to", "Related record", "text"]] },
  documents: { title: "Documents", fields: [["title", "Document title", "text", true], ["document_type", "Type", "text"], ["url", "Link", "url"], ["tags", "Tags", "text"], ["related_to", "Related record", "text"], ["notes", "Notes", "text"]] },
  feedback: { title: "Feedback & Ratings", fields: [["customer_name", "Customer name", "text", true], ["phone", "Phone", "tel"], ["rating", "Rating (1-5)", "number"], ["comment", "Feedback", "text"], ["source", "Source", "text"], ["status", "Status", "text"]] },
  help_support: { title: "Help & Support", fields: [["subject", "Subject", "text", true], ["category", "Category", "text"], ["description", "Details", "text"], ["priority", "Priority", "text"], ["status", "Status", "text"]] },
};

const emptyForm = (fields) => Object.fromEntries(fields.map(([key]) => [key, ""]));
const can = (user, module, action) => user?.role === "super_admin" || user?.is_workspace_owner || (user?.permissions?.[module] || []).includes(action);

function downloadCsv(rows, columns, filename) {
  const quote = (value) => `"${String(value ?? "").replaceAll('"', '""')}"`;
  const csv = [columns.map(([, label]) => quote(label)).join(","), ...rows.map((row) => columns.map(([key]) => quote(row[key])).join(","))].join("\r\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export default function WorkspaceRecords({ module }) {
  const { user } = useAuth();
  const config = MODULES[module];
  const isRecycleBin = module === "recycle_bin";
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const fields = config?.fields || [];

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/workspace-items/${module}`, { params: { q: query } });
      setItems(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Could not load ${config?.title || "Recycle Bin"}`);
    } finally { setBusy(false); }
  }, [module, query, config?.title]);
  useEffect(() => { load(); }, [load]);

  if (!config && !isRecycleBin) return <div className="text-sm text-muted-foreground">Unknown workspace module.</div>;

  const openForm = (item = null) => {
    setEditing(item);
    setForm(item ? Object.fromEntries(fields.map(([key]) => [key, Array.isArray(item[key]) ? item[key].join(", ") : item[key] ?? ""])) : emptyForm(fields));
    setOpen(true);
  };

  const save = async (event) => {
    event.preventDefault();
    const payload = Object.fromEntries(fields.map(([key, , type]) => {
      let value = form[key];
      if (type === "number") value = value === "" ? null : Number(value);
      if (key === "tags") value = String(value || "").split(",").map((tag) => tag.trim()).filter(Boolean);
      return [key, value];
    }));
    try {
      if (editing) await api.patch(`/workspace-items/${module}/${editing.id}`, payload);
      else await api.post(`/workspace-items/${module}`, payload);
      toast.success(editing ? "Record updated" : "Record created");
      setOpen(false);
      load();
    } catch (error) { toast.error(error.response?.data?.detail || "Could not save record"); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Move ${item.name || item.title || item.customer_name || "this record"} to Recycle Bin?`)) return;
    try {
      await api.delete(`/workspace-items/${module}/${item.id}`);
      toast.success("Moved to Recycle Bin");
      load();
    } catch (error) { toast.error(error.response?.data?.detail || "Could not delete record"); }
  };

  const restore = async (item) => {
    try {
      await api.post(`/recycle-bin/${item.resource}/${item.id}/restore`);
      toast.success("Record restored");
      load();
    } catch (error) { toast.error(error.response?.data?.detail || "Could not restore record"); }
  };

  const purge = async (item) => {
    if (!window.confirm("Permanently delete this record? This cannot be undone.")) return;
    try {
      await api.delete(`/recycle-bin/${item.resource}/${item.id}`);
      toast.success("Record permanently deleted");
      load();
    } catch (error) { toast.error(error.response?.data?.detail || "Could not delete record"); }
  };

  const columns = isRecycleBin ? [["resource", "Type"], ["name", "Name"], ["title", "Title"], ["customer_name", "Customer"], ["deleted_at", "Deleted"]] : fields.map(([key, label]) => [key, label]);

  return (
    <div className="space-y-5" data-testid={`workspace-records-${module}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">{config?.title || "Recycle Bin"}</h1>
          <p className="text-sm text-muted-foreground">{isRecycleBin ? "Restore deleted workspace records or permanently remove them." : "Company-scoped records with search, editing and CSV export."}</p>
        </div>
        <div className="flex gap-2">
          {!isRecycleBin && can(user, module, "create") && <Button data-testid={`${module}-add`} onClick={() => openForm()}><Plus size={15} className="mr-1.5" /> Add</Button>}
          <Button variant="outline" data-testid={`${module}-export`} onClick={() => downloadCsv(items, columns, `${module}.csv`)}><Download size={15} className="mr-1.5" /> Export CSV</Button>
        </div>
      </div>
      <div className="flex gap-2 max-w-xl">
        <Input data-testid={`${module}-search`} value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") setQuery(search); }} placeholder="Search records" />
        <Button variant="outline" aria-label="Search records" onClick={() => setQuery(search)}><Search size={15} /></Button>
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm min-w-[680px]">
          <thead className="bg-muted/60"><tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
            {columns.map(([key, label]) => <th key={key} className="px-3 py-3">{label}</th>)}
            <th className="px-3 py-3 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {items.map((item) => <tr key={`${item.resource || module}:${item.id}`} className="border-t hover:bg-accent/30">
              {columns.map(([key]) => <td key={key} className="px-3 py-3 max-w-64 truncate" title={String(item[key] ?? "")}>
                {key === "deleted_at" ? fmtDateTime(item[key]) : Array.isArray(item[key]) ? item[key].join(", ") : item[key] ?? "—"}
              </td>)}
              <td className="px-3 py-2 text-right whitespace-nowrap">
                {isRecycleBin ? <>
                  {can(user, "recycle_bin", "edit") && <Button variant="ghost" size="sm" aria-label="Restore record" onClick={() => restore(item)}><ArchiveRestore size={15} /></Button>}
                  {can(user, "recycle_bin", "delete") && <Button variant="ghost" size="sm" aria-label="Delete permanently" onClick={() => purge(item)}><Trash2 size={15} /></Button>}
                </> : !item.read_only && <>
                  {can(user, module, "edit") && <Button variant="ghost" size="sm" onClick={() => openForm(item)}>Edit</Button>}
                  {can(user, module, "delete") && <Button variant="ghost" size="sm" aria-label="Move to recycle bin" onClick={() => remove(item)}><Trash2 size={15} /></Button>}
                </>}
              </td>
            </tr>)}
            {!items.length && <tr><td colSpan={columns.length + 1} className="px-4 py-12 text-center text-sm text-muted-foreground">{busy ? "Loading…" : `No ${config?.title?.toLowerCase() || "deleted records"} found`}</td></tr>}
          </tbody>
        </table>
      </Card>
      {!isRecycleBin && <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${module}-form-dialog`}>
          <DialogHeader><DialogTitle>{editing ? `Edit ${config.title}` : `Add ${config.title}`}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3 max-h-[75vh] overflow-y-auto">
            {fields.map(([key, label, type = "text", required]) => <label key={key} className="block text-sm">
              <span className="text-xs text-muted-foreground">{label}</span>
              <Input data-testid={`${module}-${key}`} className="mt-1" type={type} value={form[key] ?? ""} required={!!required}
                onChange={(event) => setForm({ ...form, [key]: event.target.value })} />
            </label>)}
            <Button data-testid={`${module}-save`} type="submit" className="w-full">{editing ? "Save changes" : "Create record"}</Button>
          </form>
        </DialogContent>
      </Dialog>}
    </div>
  );
}